import { Controller } from "@hotwired/stimulus"
import { changerIcone, elementIcone } from "../lib/icones"
import { loadVendor } from "../lib/load_vendor"
import { createConsumer } from "@rails/actioncable"
import defaultConsumer from "../channels/consumer"

export default class extends Controller {
  static targets = [
    "localPreview",
    "remoteStream",
    "placeholder",
    "viewersCount",
    "viewerCount",
    "chatMessages",
    "chatInput",
    "chatForm",
    "tabBtn",
    "panel",
    "raiseHandBtn",
    "stopBtn",
    "shareBtn",
    "pollContainer",
    "pollEmpty",
    "pollBadge",
    "chatCount",
    "pinBar",
    // Host audio/video controls
    "micBtn",
    "cameraBtn",
    "cameraSelect",
    "micSelect",
    "speakerSelect",
    // Viewer fullscreen
    "videoFrame",
    "fullscreenBtn",
    // PiP (camera-in-corner during screen-share, Zoom-style)
    "pipCamera",
    "localPipCamera",
    // Connection status banner ("reconnexion…" / "déconnecté")
    "connectionBanner",
    // Path B — rich UX
    "duration",       // mm:ss timer in the header
    "reactions",      // floating emoji layer
    // Host-only Path B targets
    "pollForm",       // host's poll launcher form
    "handsList",      // host's raised hands panel
    "handsBadge",     // host's raised hands tab badge
    // Stage promotion (Path C)
    "stageList",      // host's "Sur scène" panel
    "stageBadge",     // host's stage tab badge (count of promoted users)
    "stageBanner",    // viewer banner — "Vous êtes sur scène"
    "stageControls",  // viewer mic/cam toolbar — hidden until promoted
    // Audio playback unlock (viewer) + mic level meter (host)
    "audioUnlock",    // overlay button — autoplay-blocked browsers need a tap
    "micMeter"        // 5-bar meter on the host so they see their voice land
  ]
  static values = {
    mode: String,
    sessionId: Number,
    livekitUrl: String,
    token: String,
    roomName: String,
    liveEventToken: String,
    eventSlug: String,   // public slug used by the cable connection to find the cookie
    startedAt: String, // ISO timestamp of when the live started
    selfIdentity: String, // viewer's own LiveKit identity (e.g. "registration-6")
    initialPoll: String,  // server-rendered active poll JSON (hydrate on reload)
    initialStage: String, // server-rendered list of currently-promoted identities
    initialPin:   String, // server-rendered pinned chat message JSON
    debriefUrl:   String  // host-only redirect target after "Terminer le live"
  }

  async connect() {
    if (!this.tokenValue || !this.livekitUrlValue) return

    // Host-only: warn before accidentally closing the tab mid-live. Browsers
    // ignore the custom message but show their own "Leave site?" prompt as
    // long as preventDefault + returnValue are set.
    if (this.modeValue === "host") {
      this._beforeUnload = (e) => {
        e.preventDefault()
        e.returnValue = ""
        return ""
      }
      window.addEventListener("beforeunload", this._beforeUnload)
    }

    // Latency tuning:
    // - For viewers (the audience), keep adaptiveStream OFF — it adds
    //   buffering decisions that cost ~150-300ms and isn't necessary
    //   when there's a single host stream to follow.
    // - For the host, keep adaptiveStream ON because it lets LiveKit
    //   downgrade quality gracefully under bandwidth pressure.
    // - dynacast OFF for live events to avoid the "cold-start" delay on
    //   the first viewer of a layer.
    // - videoCodec: prefer H.264 — hardware-decoded on basically every
    //   device (Android, iOS, low-end laptops), unlike VP8/VP9.
    // Lazy-load livekit-client (~475KB) only now — it's not in the universal
    // bundle. window.LiveKit is set by livekit_vendor.js (lib/load_vendor).
    try {
      await loadVendor("livekit")
    } catch (e) {
      console.error("[live-room]", e)
      return
    }
    if (!this.element.isConnected) return // left the page while loading
    const { Room, RoomEvent } = window.LiveKit

    const isHost = this.modeValue === "host"
    this.room = new Room({
      adaptiveStream: isHost,
      dynacast:       false,
      videoCaptureDefaults: { resolution: { width: 1280, height: 720 } },
      publishDefaults: {
        videoCodec:        "h264",
        // Disable simulcast for live events — we have a single tier of
        // viewers per session at this scale; the extra encoder work
        // costs CPU and adds latency on the host's machine.
        simulcast:         false,
        videoEncoding:     { maxBitrate: 1_500_000, maxFramerate: 30 },
        screenShareEncoding: { maxBitrate: 3_000_000, maxFramerate: 15 }
      }
    })
    // State for remote PiP layout: keep one screen + one camera track.
    // Recompute the layout (main + corner) whenever either changes.
    this.remoteScreenTrack = null
    this.remoteCameraTrack = null

    this.room.on(RoomEvent.ParticipantConnected, () => this.updateViewers())
    this.room.on(RoomEvent.ParticipantDisconnected, (participant) => {
      this.updateViewers()
      // Clean stale entries from the host's stage panel when a promoted
      // participant disconnects — otherwise they linger as a ghost row.
      if (this.modeValue === "host" && participant?.identity) {
        this._removeOnStage(participant.identity)
        this._removeRaisedHand(participant.identity)
      }
    })
    this.room.on(RoomEvent.TrackSubscribed, (track, publication) => this.handleTrackSubscribed(track, publication))
    this.room.on(RoomEvent.TrackUnsubscribed, (track, publication) => this.handleTrackUnsubscribed(track, publication))
    this.room.on(RoomEvent.LocalTrackPublished, (pub) => {
      this.attachLocalPreview()
      // Wire the mic meter as soon as the audio track is up. Without this
      // the host has zero way to confirm their voice is actually leaving
      // their browser — the camera preview lies (it shows their face even
      // when the OS dropped the mic).
      if (pub?.track?.kind === "audio") this._startMicMeter(pub.track)
    })
    this.room.on(RoomEvent.LocalTrackUnpublished, (pub) => {
      this.attachLocalPreview()
      if (pub?.track?.kind === "audio") this._stopMicMeter()
    })
    // setCameraEnabled(false) only mutes the track — it stays published.
    // Listen for mute/unmute too so the local preview updates when the
    // host toggles their camera/mic.
    // RoomEvent.TrackMuted/Unmuted fires for BOTH local and remote tracks.
    // We refresh both layouts so:
    //   - Host's own preview reflects their mic/camera toggles
    //   - Viewers' PiP camera disappears when the host disables it
    //     (without this, viewers see a frozen/black thumbnail because
    //     the publication is still alive, just muted)
    this.room.on(RoomEvent.TrackMuted,   () => this._refreshAllLayouts())
    this.room.on(RoomEvent.TrackUnmuted, () => this._refreshAllLayouts())
    // Autoplay policy lifeline. Mobile Safari + Chrome (strict autoplay)
    // refuse to play remote audio until the page has a user gesture. When
    // that happens LiveKit sets `canPlaybackAudio = false` and fires this
    // event; we surface a "Activer le son" button that calls startAudio()
    // inside the click handler so the browser unlocks playback.
    this.room.on(RoomEvent.AudioPlaybackStatusChanged, () => this._refreshAudioUnlock())
    // Connection lifecycle — surface state to the UI so the user knows
    // why the stream froze. LiveKit auto-reconnects internally up to a
    // configurable cap; we just reflect the state and reattach tracks
    // once it succeeds.
    this.room.on(RoomEvent.Reconnecting, () => this._showBanner("reconnecting", "Reconnexion en cours…"))
    this.room.on(RoomEvent.Reconnected,  () => {
      this._showBanner("ok", "Reconnecté")
      // Replay tracks: subscribed publications survive reconnect but
      // the local <video> elements get detached. Reattach.
      if (this.modeValue === "host") this.attachLocalPreview()
      this.refreshRemoteLayout()
      // Hide banner after a short delay so the "Reconnecté" flash is
      // perceptible without sticking around.
      setTimeout(() => this._hideBanner(), 2500)
    })
    this.room.on(RoomEvent.Disconnected, (reason) => {
      // Fully disconnected after exhausting retries. Show a manual
      // retry CTA — the user is stuck unless they reload.
      this._showBanner("error", "Connexion perdue.", { reloadable: true, sticky: true })
      console.warn("LiveKit disconnected:", reason)
    })
    // Sync the fullscreen icon with the actual fullscreen state — the
    // user may exit via Esc / OS gesture without clicking our button.
    document.addEventListener("fullscreenchange", this._onFullscreenChange = () => {
      this._setFullscreenIcon(!!document.fullscreenElement)
    })

    try {
      await this.room.connect(this.livekitUrlValue, this.tokenValue)
    } catch (e) {
      console.error("LiveKit connect failed:", e)
      return
    }

    if (this.modeValue === "host") {
      // Try camera + mic together first (single permission prompt). On
      // failure, fall back to enabling each independently so a missing
      // mic doesn't also disable an otherwise-working camera (common on
      // Linux laptops where the integrated mic isn't exposed to Chrome).
      try {
        await this.room.localParticipant.enableCameraAndMicrophone()
      } catch (e) {
        console.error("Camera+mic combined failed, trying individually:", e)
        try { await this.room.localParticipant.setCameraEnabled(true) }
        catch (camErr) { this._showMediaError("camera", camErr) }
        try { await this.room.localParticipant.setMicrophoneEnabled(true) }
        catch (micErr) { this._showMediaError("mic", micErr) }
      }
      this.attachLocalPreview()
      this.maybeDisableShareOnIOS()
      // Populate device pickers + sync mic/camera button state. Wrapped in
      // try/catch because some browsers block enumerateDevices until the
      // user has granted media permissions, which only happens after the
      // first getUserMedia call above.
      try { await this.refreshDevices() } catch (e) { console.warn("refreshDevices:", e) }
      this.refreshMediaButtons()
      navigator.mediaDevices?.addEventListener?.("devicechange", () => this.refreshDevices())
    } else {
      // Viewer side — restore "on stage" state if the server still has us
      // promoted (canPublish=true persists across reloads, but the UI
      // controls only show after a stage_changed broadcast which won't
      // arrive on a fresh connect).
      const perms = this.room.localParticipant?.permissions
      if (perms?.canPublish) {
        this._setSelfOnStage(true)
      }
    }

    if (this.hasChatMessagesTarget) this.subscribeChat()
    this.subscribeLiveEvent()
    this._startDurationTimer()
    this._hydrateInitialPoll()
    this._hydrateInitialStage()
    this._hydrateInitialPin()
    // Some browsers fire AudioPlaybackStatusChanged before the listener is
    // wired (rare race on slow networks). Reconcile once after connect so
    // the unlock button shows up if startup couldn't autoplay.
    this._refreshAudioUnlock()
  }

  // Re-render the pinned chat bar on page reload. Server passes the same
  // shape as the realtime broadcast, so we feed it straight to the
  // existing renderer.
  _hydrateInitialPin() {
    const raw = this.initialPinValue
    if (!raw || raw === "null") return
    try {
      const msg = JSON.parse(raw)
      if (msg && msg.id) this._renderPinnedBar(msg)
    } catch (_) {}
  }

  // Hydrate the host's "Sur scène" panel on page reload. Server queries
  // LiveKit for participants with canPublish=true and serializes their
  // identity + display label here, so the panel survives the host
  // navigating away and coming back.
  _hydrateInitialStage() {
    if (this.modeValue !== "host") return
    const raw = this.initialStageValue
    if (!raw || raw === "null" || raw === "[]") return
    try {
      const list = JSON.parse(raw)
      if (!Array.isArray(list)) return
      list.forEach(p => p && p.identity && this._addOnStage(p.identity, p.label))
    } catch (_) {}
  }

  // Re-render an active poll on page load. Server passes the same JSON
  // shape as the realtime broadcast, so we just feed it to renderPoll.
  _hydrateInitialPoll() {
    const raw = this.initialPollValue
    if (!raw || raw === "null") return
    try {
      const poll = JSON.parse(raw)
      if (poll && poll.id) this.renderPoll(poll)
    } catch (_) {}
  }

  // ──────────────────────────────────────────────────────────────────
  // Host audio/video controls
  // ──────────────────────────────────────────────────────────────────

  // Toggle mic publish state. LiveKit's setMicrophoneEnabled handles
  // publishing/unpublishing the audio track gracefully.
  async toggleMic() {
    if (!this.room) return
    const enabled = this.room.localParticipant.isMicrophoneEnabled
    try {
      await this.room.localParticipant.setMicrophoneEnabled(!enabled)
    } catch (e) {
      console.error("toggleMic failed:", e)
      this._showMediaError("mic", e)
    }
    this.refreshMediaButtons()
    // After a toggle the LocalAudioTrack instance can be either freshly
    // republished (meter wires automatically via LocalTrackPublished) or
    // just unmuted (no event — wire manually so the meter doesn't stay
    // dark while the host is actually talking again).
    const pub = this.room.localParticipant.getTrackPublication?.("microphone")
    if (pub?.track && !pub.isMuted) this._startMicMeter(pub.track)
    else this._stopMicMeter()
  }

  // Toggle camera publish state. Same idea as toggleMic. Force an
  // explicit attachLocalPreview() afterwards because the SDK's
  // LocalTrackUnpublished/Published events sometimes lag a tick behind
  // the await, leaving a stale frame in the preview.
  async toggleCamera() {
    if (!this.room) return
    const enabled = this.room.localParticipant.isCameraEnabled
    try {
      await this.room.localParticipant.setCameraEnabled(!enabled)
    } catch (e) {
      console.error("toggleCamera failed:", e)
      this._showMediaError("camera", e)
    }
    this.refreshMediaButtons()
    this.attachLocalPreview()
  }

  // Surface getUserMedia failures in the existing connection banner so the
  // host knows their click did nothing and *why*. NotFoundError = OS-level
  // device missing (or Chrome can't reach it via PulseAudio/PipeWire).
  // NotAllowedError = user / OS denied permission. Most common on Linux:
  // Chrome was launched before pavucontrol granted access; the user must
  // allow `localhost:3000` at chrome://settings/content/microphone.
  _showMediaError(kind, err) {
    const noun = kind === "mic" ? "microphone" : "caméra"
    let msg
    switch (err?.name) {
      case "NotFoundError":
        msg = `Aucun ${noun} détecté. Sous Linux, vérifiez pavucontrol/PipeWire et que Chrome a accès à l'appareil.`
        break
      case "NotAllowedError":
        msg = `Accès au ${noun} refusé. Cliquez sur l'icône cadenas dans la barre d'adresse pour autoriser.`
        break
      case "NotReadableError":
        msg = `${noun.charAt(0).toUpperCase() + noun.slice(1)} déjà utilisé par une autre application. Fermez Zoom/Meet/OBS et réessayez.`
        break
      default:
        msg = `Impossible d'activer le ${noun} : ${err?.message || err?.name || "erreur inconnue"}`
    }
    this._showBanner("error", msg, { sticky: true })
  }

  // Switch active device (videoinput / audioinput / audiooutput).
  selectCamera(event)  { this.switchDevice("videoinput",  event.target.value) }
  selectMic(event)     { this.switchDevice("audioinput",  event.target.value) }
  selectSpeaker(event) { this.switchDevice("audiooutput", event.target.value) }

  async switchDevice(kind, deviceId) {
    if (!this.room || !deviceId) return
    try {
      await this.room.switchActiveDevice(kind, deviceId)
    } catch (e) {
      console.error(`switchDevice(${kind}) failed:`, e)
    }
  }

  // Enumerate connected devices and fill the <select> elements. Reads the
  // currently-selected device for each kind and pre-selects it so the
  // dropdown reflects reality on first paint.
  async refreshDevices() {
    if (!navigator.mediaDevices?.enumerateDevices) return
    const devices = await navigator.mediaDevices.enumerateDevices()

    const fill = (target, kind, currentId) => {
      if (!target) return
      const matching = devices.filter(d => d.kind === kind && d.deviceId)
      target.innerHTML = ""
      if (matching.length === 0) {
        const opt = document.createElement("option")
        opt.textContent = "Aucun appareil détecté"
        opt.disabled = true
        target.appendChild(opt)
        return
      }
      matching.forEach(d => {
        const opt = document.createElement("option")
        opt.value = d.deviceId
        // Browsers return empty labels until the user has granted media
        // permissions — fall back to a friendly placeholder so the menu
        // isn't blank.
        opt.textContent = d.label || `${kind} ${d.deviceId.slice(0, 6)}`
        if (d.deviceId === currentId) opt.selected = true
        target.appendChild(opt)
      })
    }

    fill(this.hasCameraSelectTarget  ? this.cameraSelectTarget  : null, "videoinput",  this.currentDeviceId("videoinput"))
    fill(this.hasMicSelectTarget     ? this.micSelectTarget     : null, "audioinput",  this.currentDeviceId("audioinput"))
    fill(this.hasSpeakerSelectTarget ? this.speakerSelectTarget : null, "audiooutput", this.currentDeviceId("audiooutput"))
  }

  // LiveKit doesn't expose the active deviceId directly across SDK
  // versions, so we read it from the published track's MediaStreamTrack
  // settings when available — falls back to undefined which means the
  // select will pick the first option.
  currentDeviceId(kind) {
    if (!this.room) return undefined
    const lp = this.room.localParticipant
    if (kind === "videoinput") {
      const pub = Array.from(lp.videoTrackPublications.values()).find(p => p.source === "camera")
      return pub?.track?.mediaStreamTrack?.getSettings?.().deviceId
    }
    if (kind === "audioinput") {
      const pub = Array.from(lp.audioTrackPublications.values()).find(p => p.source === "microphone")
      return pub?.track?.mediaStreamTrack?.getSettings?.().deviceId
    }
    return undefined
  }

  // Sync the mic/camera button visual state with the actual room state.
  // We use FA icon swaps + an `is-off` class for styling.
  refreshMediaButtons() {
    if (!this.room) return
    const lp = this.room.localParticipant

    if (this.hasMicBtnTarget) {
      const on = lp.isMicrophoneEnabled
      this.micBtnTarget.classList.toggle("is-off", !on)
      this.micBtnTarget.setAttribute("aria-pressed", String(on))
      changerIcone(this.micBtnTarget.querySelector(".lucide"), on ? "mic" : "mic-off")
      this.micBtnTarget.title = on ? "Couper le micro" : "Activer le micro"
    }

    if (this.hasCameraBtnTarget) {
      const on = lp.isCameraEnabled
      this.cameraBtnTarget.classList.toggle("is-off", !on)
      this.cameraBtnTarget.setAttribute("aria-pressed", String(on))
      changerIcone(this.cameraBtnTarget.querySelector(".lucide"), on ? "video" : "video-off")
      this.cameraBtnTarget.title = on ? "Couper la caméra" : "Activer la caméra"
    }
  }

  disconnect() {
    if (this._beforeUnload) {
      window.removeEventListener("beforeunload", this._beforeUnload)
      this._beforeUnload = null
    }
    if (this.room) this.room.disconnect()
    if (this.chatSub) this.chatSub.unsubscribe()
    if (this.liveEventSub) this.liveEventSub.unsubscribe()
    // Token-scoped consumers don't share state with anything else, so it's
    // safe to fully tear them down here. Default consumer keeps living.
    if (this.chatConsumer && this.chatConsumer !== defaultConsumer) {
      this.chatConsumer.disconnect()
    }
    if (this._onFullscreenChange) {
      document.removeEventListener("fullscreenchange", this._onFullscreenChange)
    }
    if (this.durationInterval) clearInterval(this.durationInterval)
    this._stopMicMeter()
  }

  // ──────────────────────────────────────────────────────────────────
  // Audio playback unlock (viewer-side autoplay policy)
  // ──────────────────────────────────────────────────────────────────

  // Shows / hides the "Activer le son" overlay based on whether LiveKit
  // managed to start audio playback. iOS Safari + Chrome strict-autoplay
  // need an explicit click after a user gesture.
  _refreshAudioUnlock() {
    if (!this.hasAudioUnlockTarget) return
    const canPlay = this.room?.canPlaybackAudio !== false
    this.audioUnlockTarget.classList.toggle("hidden", canPlay)
  }

  // Stimulus action — wired to the overlay button. Browsers only let
  // startAudio() succeed when called synchronously inside a user gesture,
  // hence the dedicated handler rather than calling it on connect.
  async unlockAudio() {
    if (!this.room) return
    try {
      await this.room.startAudio()
    } catch (e) {
      console.warn("startAudio failed:", e)
    }
    this._refreshAudioUnlock()
  }

  // ──────────────────────────────────────────────────────────────────
  // Mic level meter (host-side)
  // ──────────────────────────────────────────────────────────────────

  // Pipes the local mic's MediaStreamTrack through a Web Audio analyser
  // so we can light up the 5-bar meter in the toolbar. This is the host's
  // only reliable signal that their voice is actually leaving the laptop
  // — the camera preview keeps painting their face even when PulseAudio
  // dropped the mic.
  _startMicMeter(audioTrack) {
    if (!this.hasMicMeterTarget) return
    this._stopMicMeter()
    const stream = audioTrack?.mediaStream || (audioTrack?.mediaStreamTrack && new MediaStream([audioTrack.mediaStreamTrack]))
    if (!stream) return
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext
      if (!AudioCtx) return
      const ctx = new AudioCtx()
      const source = ctx.createMediaStreamSource(stream)
      const analyser = ctx.createAnalyser()
      analyser.fftSize = 256
      analyser.smoothingTimeConstant = 0.6
      source.connect(analyser)
      const buf = new Uint8Array(analyser.frequencyBinCount)
      const bars = Array.from(this.micMeterTarget.querySelectorAll(".lr-mic-bar"))

      const tick = () => {
        analyser.getByteTimeDomainData(buf)
        // RMS over the time-domain samples. 128 is zero-line; deviation
        // from it scales with loudness. ~0 silence, ~0.5 loud speaking.
        let sum = 0
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i] - 128) / 128
          sum += v * v
        }
        const rms   = Math.sqrt(sum / buf.length)
        const level = Math.min(1, rms * 3)
        const lit   = Math.round(level * bars.length)
        bars.forEach((bar, idx) => bar.classList.toggle("on", idx < lit))
        this._micMeterRaf = requestAnimationFrame(tick)
      }
      this._micMeterCtx = ctx
      this._micMeterSource = source
      this._micMeterRaf = requestAnimationFrame(tick)
      this.micMeterTarget.classList.remove("muted")
    } catch (e) {
      console.warn("mic meter setup failed:", e)
    }
  }

  _stopMicMeter() {
    if (this._micMeterRaf) cancelAnimationFrame(this._micMeterRaf)
    this._micMeterRaf = null
    if (this._micMeterSource) { try { this._micMeterSource.disconnect() } catch (_) {} }
    this._micMeterSource = null
    if (this._micMeterCtx)    { try { this._micMeterCtx.close() }       catch (_) {} }
    this._micMeterCtx = null
    if (this.hasMicMeterTarget) {
      this.micMeterTarget.classList.add("muted")
      this.micMeterTarget.querySelectorAll(".lr-mic-bar.on").forEach(b => b.classList.remove("on"))
    }
  }

  // Toggle fullscreen on the video frame so the chat can stay visible
  // when collapsing back. Falls back gracefully on browsers that don't
  // support requestFullscreen on arbitrary elements (iOS Safari).
  toggleFullscreen() {
    const target = this.hasVideoFrameTarget ? this.videoFrameTarget : this.element
    if (document.fullscreenElement) {
      document.exitFullscreen?.()
      this._setFullscreenIcon(false)
      return
    }
    const req = target.requestFullscreen ||
                target.webkitRequestFullscreen ||
                target.mozRequestFullScreen ||
                target.msRequestFullscreen
    if (req) {
      req.call(target).then(() => this._setFullscreenIcon(true))
                      .catch(e => console.warn("fullscreen failed:", e))
    } else {
      // Some Safari versions only allow fullscreen on <video> elements;
      // try the inner video as a last resort.
      const video = target.querySelector("video")
      video?.webkitEnterFullscreen?.()
    }
  }

  _setFullscreenIcon(isFullscreen) {
    if (!this.hasFullscreenBtnTarget) return
    changerIcone(this.fullscreenBtnTarget.querySelector(".lucide"), isFullscreen ? "minimize" : "maximize")
  }

  // ──────────────────────────────────────────────────────────────────
  // Connection status banner (LiveKit + ActionCable)
  // ──────────────────────────────────────────────────────────────────
  _showBanner(level, message, opts = {}) {
    if (!this.hasConnectionBannerTarget) return
    const t = this.connectionBannerTarget

    // Reset state
    t.classList.remove("is-error", "is-reconnecting", "is-ok", "hidden")
    t.classList.add(`is-${level}`)

    // Build content with safe DOM ops (no innerHTML to keep CSP clean).
    t.replaceChildren()
    const span = document.createElement("span")
    span.textContent = message
    t.appendChild(span)

    if (opts.reloadable) {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.textContent = "Recharger"
      btn.className = "lr-banner-cta"
      btn.addEventListener("click", () => window.location.reload())
      t.appendChild(btn)
    }

    if (this._bannerTimeout) clearTimeout(this._bannerTimeout)
    if (!opts.sticky && level === "ok") {
      this._bannerTimeout = setTimeout(() => this._hideBanner(), 2500)
    }
  }

  _hideBanner() {
    if (!this.hasConnectionBannerTarget) return
    this.connectionBannerTarget.classList.add("hidden")
  }

  _onChatConnected() {
    // Only flash a banner if we were previously in a degraded state
    // — otherwise the initial connection would always show "ok".
    if (this._chatWasDisconnected) {
      this._showBanner("ok", "Chat reconnecté")
      this._chatWasDisconnected = false
    }
  }

  _onChatDisconnected() {
    this._chatWasDisconnected = true
    this._showBanner("reconnecting", "Reconnexion du chat…")
  }

  // Re-renders both the host's own preview AND the viewer's remote layout.
  // Cheap idempotent operation — called on track mute/unmute events
  // because RoomEvent.TrackMuted fires for both local and remote tracks
  // and either side might need a layout refresh.
  _refreshAllLayouts() {
    if (this.modeValue === "host") this.attachLocalPreview()
    if (this.refreshRemoteLayout)  this.refreshRemoteLayout()
  }

  // ──────────────────────────────────────────────────────────────────
  // Path B — Tabs (Chat / Q&A / Sondage / Ressources)
  // ──────────────────────────────────────────────────────────────────
  switchTab(event) {
    const target = event.currentTarget.dataset.tab
    this.tabBtnTargets.forEach(b => b.classList.toggle("is-active", b.dataset.tab === target))
    this.panelTargets.forEach(p => {
      const isActive = p.dataset.tab === target
      p.classList.toggle("is-active", isActive)
      p.hidden = !isActive
    })
    if (target === "poll") this._hidePollBadge()
  }

  _showPollBadge() {
    if (!this.hasPollBadgeTarget) return
    // If the poll tab is already active, don't bother flagging it.
    const activeTabBtn = this.tabBtnTargets.find(b => b.classList.contains("is-active"))
    if (activeTabBtn?.dataset.tab === "poll") return
    this.pollBadgeTarget.textContent = "•"
    this.pollBadgeTarget.classList.remove("hidden")
  }

  _hidePollBadge() {
    if (this.hasPollBadgeTarget) this.pollBadgeTarget.classList.add("hidden")
  }

  // ──────────────────────────────────────────────────────────────────
  // Path B — Live event channel (Q&A, raise hand, reactions)
  // ──────────────────────────────────────────────────────────────────
  subscribeLiveEvent() {
    const consumer = this.chatConsumer || defaultConsumer
    this.liveEventSub = consumer.subscriptions.create(
      { channel: "LiveEventChannel", session_id: this.sessionIdValue },
      {
        received: (data) => this.handleLiveEventMessage(data),
        raise()          { this.perform("raise_hand") },
        lower()          { this.perform("lower_hand") },
        reactWith(emoji) { this.perform("react",       { emoji }) },
        // Stage promotion — host-only RPCs
        promote(identity, label) { this.perform("promote", { identity, label }) },
        demote(identity)         { this.perform("demote",  { identity }) },
        muteOne(identity)        { this.perform("mute_one", { identity }) },
        muteAll()                { this.perform("mute_all") }
      }
    )
  }

  handleLiveEventMessage(data) {
    if (!data || !data.kind) return
    switch (data.kind) {
      case "reaction":      this._spawnReaction(data.emoji); break
      case "hand_raised":   this._addRaisedHand(data.identity, data.attendee); break
      case "hand_lowered":  this._removeRaisedHand(data.identity); break
      case "stage_changed": this._handleStageChanged(data); break
      case "muted":         this._handleMuted(data); break
      case "muted_all":     this._handleMutedAll(); break
      case "mute_failed":   this._handleMuteFailed(data); break
      case "promote_failed":
      case "demote_failed": this._handleStageFailed(data); break
    }
  }

  // promote/demote round-trip to LiveKit can fail (network blip, room gone).
  // When it does, the server emits this so the host knows their optimistic
  // "Sur scène" update doesn't match reality and they should retry.
  _handleStageFailed(data) {
    if (this.modeValue !== "host") return
    const verb = data.kind === "promote_failed" ? "promotion" : "rétrogradation"
    this._showBanner("error", `Échec de la ${verb}. Réessayez.`, { sticky: false })
    setTimeout(() => this._hideBanner(), 4000)
  }

  // The server fires this when the async LiveKit call backing a mute_one /
  // mute_all request blew up. Show the host a corrective banner so they
  // know the optimistic UI doesn't match reality.
  _handleMuteFailed(data) {
    if (this.modeValue !== "host") return
    const msg = data.scope === "all"
      ? "Échec du mute global. Réessayez."
      : "Échec du mute. Réessayez."
    this._showBanner("error", msg, { sticky: false })
    setTimeout(() => this._hideBanner(), 4000)
  }

  // Stage broadcasts — both host and viewer receive these. Host updates the
  // stage panel; viewers check if it's about them and toggle their controls.
  _handleStageChanged(data) {
    if (!data.identity) return
    if (this.modeValue === "host") {
      if (data.on_stage) this._addOnStage(data.identity, data.label)
      else               this._removeOnStage(data.identity)
      // Promoting from a raised hand — auto-clear from hands list.
      if (data.on_stage) this._removeRaisedHand(data.identity)
    } else if (data.identity === this.selfIdentityValue) {
      // It's about us — show or hide our own mic/cam controls.
      this._setSelfOnStage(!!data.on_stage)
    }
  }

  _handleMuted(data) {
    // Only the muted viewer needs a banner. The host sees the participant's
    // track go silent via LiveKit's TrackMuted event — a separate banner
    // here would cascade and clutter their UI if they mute several in a row.
    if (this.modeValue !== "host" && data.identity === this.selfIdentityValue) {
      this._showBanner("info", "Votre micro a été coupé par l'animateur.", { sticky: false })
      setTimeout(() => this._hideBanner(), 4000)
    }
  }

  _handleMutedAll() {
    if (this.modeValue !== "host") {
      this._showBanner("info", "L'animateur a coupé tous les micros.", { sticky: false })
      setTimeout(() => this._hideBanner(), 4000)
    }
  }

  // Viewer reveals/hides its mic+cam toolbar when promoted/demoted. LiveKit
  // delivers a ParticipantPermissionsChanged event on the room; the SDK
  // re-issues the subscriber→publisher upgrade automatically when canPublish
  // flips, so all we need to do is render the buttons. When demoted, LiveKit
  // force-unpublishes — we just hide the UI.
  _setSelfOnStage(onStage) {
    if (this.hasStageControlsTarget) this.stageControlsTarget.classList.toggle("hidden", !onStage)
    if (this.hasStageBannerTarget)   this.stageBannerTarget.classList.toggle("hidden", !onStage)
    if (!onStage && this.room) {
      // Belt-and-suspenders: explicitly disable locally even though the
      // server already revoked permissions. Avoids a flash of camera if
      // LiveKit's permission propagation lags a frame.
      this.room.localParticipant.setMicrophoneEnabled(false).catch(() => {})
      this.room.localParticipant.setCameraEnabled(false).catch(() => {})
    }
    this.refreshMediaButtons?.()
  }

  // Host stage panel — track on-stage participants in a Map and render
  // a dedicated list with "Mute" + "Retirer" actions per row.
  _addOnStage(identity, label) {
    this._onStage ||= new Map()
    this._onStage.set(identity, label || identity)
    this._renderStage()
  }

  _removeOnStage(identity) {
    if (!this._onStage) return
    this._onStage.delete(identity)
    this._renderStage()
  }

  _renderStage() {
    if (!this.hasStageListTarget) return
    const stage = this._onStage || new Map()
    this.stageListTarget.replaceChildren()

    if (stage.size === 0) {
      const empty = document.createElement("li")
      empty.className = "lr-empty"
      empty.textContent = "Personne sur scène pour le moment."
      this.stageListTarget.appendChild(empty)
    } else {
      stage.forEach((label, identity) => {
        const li = document.createElement("li")
        li.className = "lr-stage-item"
        li.dataset.identity = identity

        const name = document.createElement("span")
        name.className = "lr-stage-name"
        name.textContent = label
        li.appendChild(name)

        const muteBtn = document.createElement("button")
        muteBtn.type = "button"
        muteBtn.className = "lr-stage-mute"
        muteBtn.title = "Couper le micro"
        muteBtn.dataset.identity = identity
        muteBtn.dataset.action = "click->live-room#muteOnStage"
        const muteIcon = elementIcone("mic-off")
        muteBtn.appendChild(muteIcon)
        li.appendChild(muteBtn)

        const removeBtn = document.createElement("button")
        removeBtn.type = "button"
        removeBtn.className = "lr-stage-remove"
        removeBtn.title = "Retirer de la scène"
        removeBtn.dataset.identity = identity
        removeBtn.dataset.action = "click->live-room#demoteFromStage"
        const removeIcon = elementIcone("x")
        removeBtn.appendChild(removeIcon)
        li.appendChild(removeBtn)

        this.stageListTarget.appendChild(li)
      })
    }

    if (this.hasStageBadgeTarget) {
      if (stage.size > 0) {
        this.stageBadgeTarget.textContent = String(stage.size)
        this.stageBadgeTarget.classList.remove("hidden")
      } else {
        this.stageBadgeTarget.classList.add("hidden")
      }
    }
  }

  // Stimulus actions wired from the host UI.
  promoteFromHand(event) {
    const identity = event.currentTarget.dataset.identity
    const label    = event.currentTarget.dataset.label
    if (identity && this.liveEventSub) this.liveEventSub.promote(identity, label)
  }

  demoteFromStage(event) {
    const identity = event.currentTarget.dataset.identity
    if (identity && this.liveEventSub) this.liveEventSub.demote(identity)
  }

  muteOnStage(event) {
    const identity = event.currentTarget.dataset.identity
    if (identity && this.liveEventSub) this.liveEventSub.muteOne(identity)
  }

  muteAll() {
    if (!this.liveEventSub) return
    if (!window.confirm("Couper le micro de tous les invités sur scène ?")) return
    this.liveEventSub.muteAll()
  }

  // ──────────────────────────────────────────────────────────────────
  // Path B — Reactions (floating emojis)
  // ──────────────────────────────────────────────────────────────────
  sendReaction(event) {
    const emoji = event.currentTarget.dataset.emoji
    if (!emoji || !this.liveEventSub) return
    this.liveEventSub.reactWith(emoji)
    // Optimistic local rendering — also handle our own broadcast echo.
    this._spawnReaction(emoji)
  }

  _spawnReaction(emoji) {
    if (!this.hasReactionsTarget) return
    const el = document.createElement("span")
    el.className = "lr-react-emoji"
    el.textContent = emoji
    el.style.left = `${10 + Math.random() * 80}%`
    this.reactionsTarget.appendChild(el)
    setTimeout(() => el.remove(), 3000)
  }

  // ──────────────────────────────────────────────────────────────────
  // Path B — Raise hand
  // ──────────────────────────────────────────────────────────────────
  toggleRaiseHand(event) {
    if (!this.liveEventSub) return
    // Debounce rapid clicks: a quick double-click would burn 2 raises in
    // the server-side rate-limit window for nothing visible.
    const now = Date.now()
    if (this._lastRaiseToggle && now - this._lastRaiseToggle < 300) return
    this._lastRaiseToggle = now

    const btn = event.currentTarget
    const wasRaised = btn.getAttribute("aria-pressed") === "true"
    if (wasRaised) {
      this.liveEventSub.lower()
      btn.setAttribute("aria-pressed", "false")
    } else {
      this.liveEventSub.raise()
      btn.setAttribute("aria-pressed", "true")
    }
  }

  // ──────────────────────────────────────────────────────────────────
  // Path B — Duration counter (header)
  // ──────────────────────────────────────────────────────────────────
  _startDurationTimer() {
    if (!this.hasDurationTarget) return
    if (!this.startedAtValue) return
    this.durationStart = new Date(this.startedAtValue).getTime()
    this._tickDuration()
    this.durationInterval = setInterval(() => this._tickDuration(), 1000)
  }

  _tickDuration() {
    const elapsed = Math.max(0, Math.floor((Date.now() - this.durationStart) / 1000))
    const h = Math.floor(elapsed / 3600)
    const m = Math.floor((elapsed % 3600) / 60)
    const s = elapsed % 60
    const pad = (n) => String(n).padStart(2, "0")
    this.durationTarget.textContent = h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`
  }


  // Host launches a poll. Posts directly to /live_polls (existing
  // controller); the response triggers a broadcast on LiveChatChannel
  // (kind: poll_started) which existing renderPoll() picks up.
  async launchPoll(event) {
    event.preventDefault()
    if (!this.hasPollFormTarget) return
    const form = this.pollFormTarget
    const formData = new FormData(form)
    const question = formData.get("question")
    const options  = formData.getAll("options[]").filter(o => o.trim().length > 0)
    if (!question || options.length < 2) return

    const body = new URLSearchParams()
    body.append("live_session_id", this.sessionIdValue)
    body.append("question", question)
    options.forEach(o => body.append("options[]", o))

    try {
      const res = await fetch(`/live_sessions/${this.sessionIdValue}/live_polls`, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.csrf(),
          "Accept": "application/json",
          "Content-Type": "application/x-www-form-urlencoded"
        },
        body: body.toString()
      })
      if (res.ok) form.reset()
      else console.warn("launchPoll failed:", res.status)
    } catch (e) {
      console.error("launchPoll error:", e)
    }
  }

  // Raised hands tracking. Maintains a Set of identities and re-renders
  // the host's panel + tab badge whenever it changes.
  _addRaisedHand(identity, label) {
    if (!identity) return
    this._raisedHands ||= new Map()
    this._raisedHands.set(identity, label || "Invité")
    this._renderRaisedHands()
  }

  _removeRaisedHand(identity) {
    if (!identity || !this._raisedHands) return
    this._raisedHands.delete(identity)
    this._renderRaisedHands()
  }

  _renderRaisedHands() {
    if (!this.hasHandsListTarget) return
    const hands = this._raisedHands || new Map()

    this.handsListTarget.replaceChildren()
    if (hands.size === 0) {
      const empty = document.createElement("li")
      empty.className = "lr-empty"
      empty.textContent = "Personne n'a levé la main pour le moment."
      this.handsListTarget.appendChild(empty)
    } else {
      hands.forEach((label, identity) => {
        const li = document.createElement("li")
        li.className = "lr-hand-item"
        li.dataset.identity = identity

        const icon = elementIcone("hand")
        li.appendChild(icon)

        const name = document.createElement("span")
        name.className = "lr-hand-name"
        name.textContent = label
        li.appendChild(name)

        // Inviter sur scène — flips canPublish=true server-side. The
        // raised-hand entry is removed automatically via stage_changed.
        const invite = document.createElement("button")
        invite.type = "button"
        invite.className = "lr-hand-invite"
        invite.title = "Inviter sur scène"
        invite.dataset.identity = identity
        invite.dataset.label    = label
        invite.dataset.action   = "click->live-room#promoteFromHand"
        const inviteIcon = elementIcone("mic")
        invite.appendChild(inviteIcon)
        li.appendChild(invite)

        const dismiss = document.createElement("button")
        dismiss.type = "button"
        dismiss.className = "lr-hand-dismiss"
        dismiss.title = "Marquer comme traité"
        dismiss.dataset.identity = identity
        dismiss.dataset.action = "click->live-room#dismissHand"
        const dismissIcon = elementIcone("check")
        dismiss.appendChild(dismissIcon)
        li.appendChild(dismiss)

        this.handsListTarget.appendChild(li)
      })
    }

    // Multiple `handsBadge` targets are allowed — the host view shows the
    // counter both in the topbar stat strip and in the left-rail eyebrow.
    // Tab badges hide at 0 (old behavior); plain counters always show the
    // number so the eyebrow "Mains levées · 0" stays legible.
    this.handsBadgeTargets.forEach((el) => {
      el.textContent = String(hands.size)
      const isTabBadge = el.classList.contains("lr-tab-badge")
      if (isTabBadge) {
        el.classList.toggle("hidden", hands.size === 0)
      } else {
        el.classList.remove("hidden")
      }
    })
  }

  // Host clicks ✓ on a raised hand → remove from local list. Doesn't
  // notify the participant; they keep their hand raised on their side
  // until they lower it themselves.
  dismissHand(event) {
    const identity = event.currentTarget.dataset.identity
    this._removeRaisedHand(identity)
  }

  async stop() {
    if (!confirm("Arrêter le live ? Les spectateurs seront déconnectés.")) return
    // Voluntary stop — drop the beforeunload guard so the post-stop redirect
    // doesn't trigger the browser's "Leave site?" prompt.
    if (this._beforeUnload) {
      window.removeEventListener("beforeunload", this._beforeUnload)
      this._beforeUnload = null
    }
    let res
    try {
      res = await fetch(`/live_sessions/${this.sessionIdValue}`, {
        method:  "DELETE",
        headers: { "X-CSRF-Token": this.csrf() }
      })
    } catch (e) {
      this._showBanner("error", "Impossible d'arrêter le live : connexion réseau perdue.", { sticky: true })
      return
    }
    if (!res.ok) {
      // 404 = session already deleted elsewhere → safe to navigate
      // anything else (403 / 5xx) → surface the failure instead of
      // silently sending the host to /dashboard and leaving LiveKit alive.
      if (res.status === 404) {
        if (this.room) this.room.disconnect()
        window.location.href = this.debriefUrlValue || "/dashboard"
        return
      }
      this._showBanner("error", `Échec de l'arrêt du live (HTTP ${res.status}). Réessayez ou contactez le support.`, { sticky: true })
      return
    }
    if (this.room) this.room.disconnect()
    window.location.href = "/dashboard"
  }

  async share() {
    if (this.modeValue !== "host") return
    try {
      await this.room.localParticipant.setScreenShareEnabled(true)
    } catch (e) {
      console.error("Screen share failed:", e)
    }
  }

  attachLocalPreview() {
    if (!this.hasLocalPreviewTarget) return
    const pubs = Array.from(this.room.localParticipant.videoTrackPublications.values())

    // setCameraEnabled(false) may keep the publication alive but mute
    // the underlying track (depending on LiveKit version). Treat muted
    // publications as "not available" so the preview clears.
    const usable = (pub) => pub && pub.track && !pub.isMuted && !pub.track.isMuted
    const screenPub = pubs.find(p => p.source === "screen_share" && usable(p))
    const cameraPub = pubs.find(p => p.source === "camera"       && usable(p))

    const mainTrack = screenPub?.track || cameraPub?.track
    const pipTrack  = screenPub?.track ? cameraPub?.track : null

    // Always rip out any pre-existing <video> element first. Without this,
    // the SDK's internal element cache can leave a stale (frozen, often
    // black) frame after a track is muted/republished. We also clear
    // srcObject explicitly so the browser drops the GPU surface.
    this._purgeVideos(this.localPreviewTarget)
    if (this.hasLocalPipCameraTarget) this._purgeVideos(this.localPipCameraTarget)

    if (mainTrack) {
      const el = mainTrack.attach()
      el.style.width  = "100%"
      el.style.height = "100%"
      el.style.objectFit = "contain"
      this.localPreviewTarget.appendChild(el)
    }

    if (this.hasLocalPipCameraTarget) {
      if (pipTrack) {
        const el = pipTrack.attach()
        el.style.width  = "100%"
        el.style.height = "100%"
        el.style.objectFit = "cover"
        this.localPipCameraTarget.appendChild(el)
        this.localPipCameraTarget.classList.remove("hidden")
      } else {
        this.localPipCameraTarget.classList.add("hidden")
      }
    }
  }

  // Detach + remove every <video> element under `el`, freeing the
  // underlying GPU resources. Used to avoid stale frames when LiveKit
  // mutes/unmutes/republishes a track.
  _purgeVideos(el) {
    if (!el) return
    Array.from(el.querySelectorAll("video")).forEach(v => {
      try { v.srcObject = null } catch (_e) {}
      v.remove()
    })
  }

  // ──────────────────────────────────────────────────────────────────
  // Remote tracks (viewer side) with Zoom-style PiP
  // ──────────────────────────────────────────────────────────────────
  handleTrackSubscribed(track, publication) {
    if (track.kind === "audio") {
      // Audio always attaches (hidden); needed for sound to play.
      const el = track.attach()
      el.style.display = "none"
      if (this.hasRemoteStreamTarget) this.remoteStreamTarget.appendChild(el)
      return
    }
    if (track.kind !== "video") return

    if (publication?.source === "screen_share") {
      this.remoteScreenTrack = track
    } else if (publication?.source === "camera") {
      this.remoteCameraTrack = track
    }
    this.refreshRemoteLayout()
  }

  handleTrackUnsubscribed(track, publication) {
    if (publication?.source === "screen_share" && this.remoteScreenTrack === track) {
      this.remoteScreenTrack = null
    } else if (publication?.source === "camera" && this.remoteCameraTrack === track) {
      this.remoteCameraTrack = null
    }
    track.detach()
    this.refreshRemoteLayout()
  }

  // Main slot shows screen-share if active, otherwise camera. PiP slot
  // (small corner overlay) only shows camera *when* screen-share is also
  // active — Zoom/Meet/Streamyard pattern. Muted tracks are treated as
  // absent so the host disabling their camera makes it disappear from
  // viewers' PiP instead of showing a frozen black frame.
  refreshRemoteLayout() {
    const liveTrack = (t) => (t && !t.isMuted) ? t : null
    const screen   = liveTrack(this.remoteScreenTrack)
    const camera   = liveTrack(this.remoteCameraTrack)
    const mainTrack = screen || camera
    const pipTrack  = screen ? camera : null

    // Hide the "Connexion au flux en cours…" placeholder as soon as we
    // actually have a track to display. Without this the placeholder
    // overlays the main video because both share absolute positioning.
    if (this.hasPlaceholderTarget) {
      this.placeholderTarget.style.display = mainTrack ? "none" : ""
    }

    if (this.hasRemoteStreamTarget) {
      // Preserve audio elements that were appended; only swap the video.
      const audios = Array.from(this.remoteStreamTarget.querySelectorAll("audio"))
      this.remoteStreamTarget.replaceChildren(...audios)
      if (mainTrack) {
        const el = mainTrack.attach()
        el.style.width  = "100%"
        el.style.height = "100%"
        el.style.objectFit = "contain"
        this._minimisePlayoutDelay(el)
        this.remoteStreamTarget.appendChild(el)
      }
    }

    if (this.hasPipCameraTarget) {
      this.pipCameraTarget.replaceChildren()
      if (pipTrack) {
        const el = pipTrack.attach()
        el.style.width  = "100%"
        el.style.height = "100%"
        el.style.objectFit = "cover"
        this._minimisePlayoutDelay(el)
        this.pipCameraTarget.appendChild(el)
        this.pipCameraTarget.classList.remove("hidden")
      } else {
        this.pipCameraTarget.classList.add("hidden")
      }
    }
  }

  // Tell the browser to minimise its WebRTC jitter buffer for this
  // element. Default ~200ms; setting 0 typically results in 50-100ms
  // actual buffer (browser still adds the bare minimum for resilience).
  // Saves ~150ms of perceived latency on the viewer side at the cost
  // of slightly more frame drops under poor network — acceptable for
  // a "talking head" live broadcast use case.
  _minimisePlayoutDelay(el) {
    if (!el || el.tagName !== "VIDEO") return
    try {
      el.playoutDelayHint = 0
      // Some browsers expose `disableRemotePlayback` which removes a
      // round-trip handshake when chromecasting/airplay isn't needed.
      el.disableRemotePlayback = true
    } catch (_e) { /* no-op on unsupported browsers */ }
  }

  // Legacy single-track attach (kept for the non-PiP host preview path).
  attachTrack(track) {
    if (!this.hasRemoteStreamTarget) return
    if (track.kind === "video" || track.kind === "audio") {
      const el = track.attach()
      if (track.kind === "video") {
        el.style.width = "100%"
        el.style.height = "100%"
        el.style.objectFit = "contain"
      }
      this.remoteStreamTarget.appendChild(el)
      if (this.hasPlaceholderTarget) this.placeholderTarget.style.display = "none"
    }
  }

  updateViewers() {
    const count = this.room ? this.room.remoteParticipants.size : 0
    if (this.hasViewersCountTarget) this.viewersCountTarget.textContent = `${count} spectateur(s)`
    if (this.hasViewerCountTarget)  this.viewerCountTarget.textContent  = String(count)
  }

  sendChat(event) {
    event.preventDefault()
    if (!this.chatSub || !this.hasChatInputTarget) return
    const msg = this.chatInputTarget.value.trim()
    if (!msg) return
    this.chatSub.speak(msg)
    this.chatInputTarget.value = ""
  }

  subscribeChat() {
    // Anonymous viewers (public webinar / invite-only) authenticate to
    // ActionCable via the signed HttpOnly cookie set by `redirect_clean_url`.
    // We only hint at the event slug on the WS URL so the connection can
    // pick the right per-event cookie (`live_event_public_<slug>` /
    // `live_event_invite_<slug>`). The token itself never appears in URLs,
    // browser history, devtools network panel, or upstream proxy logs.
    // Authenticated users (host, students of a course-private live) reuse
    // the default consumer which relies on the Warden cookie.
    const consumer = this.eventSlugValue
      ? createConsumer(`/cable?live_event_slug=${encodeURIComponent(this.eventSlugValue)}`)
      : defaultConsumer
    this.chatConsumer = consumer

    this.chatSub = consumer.subscriptions.create(
      { channel: "LiveChatChannel", session_id: this.sessionIdValue },
      {
        received:     (data) => this.renderChatMessage(data),
        connected:    () => this._onChatConnected(),
        disconnected: () => this._onChatDisconnected(),
        rejected:     () => this._showBanner("error", "Connexion chat refusée.", { sticky: true }),
        speak(message)   { this.perform("speak", { message }) },
        pinMessage(id)   { this.perform("pin",   { message_id: id }) },
        unpinMessage()   { this.perform("unpin") }
      }
    )
    // Legacy form binding kept for the host view (which has no Stimulus
    // action wired on its chat form). The viewer view dispatches
    // `submit->live-room#sendChat` so this fallback is harmless there.
    if (this.hasChatFormTarget && !this.chatFormTarget.dataset.action?.includes("live-room#sendChat")) {
      this.chatFormTarget.addEventListener("submit", (e) => {
        e.preventDefault()
        const msg = this.chatInputTarget.value.trim()
        if (!msg) return
        this.chatSub.speak(msg)
        this.chatInputTarget.value = ""
      })
    }
  }

  renderChatMessage(data) {
    if (!data) return
    if (data.kind === "poll_started"  && data.poll)    { this.renderPoll(data.poll); this._showPollBadge(); return }
    if (data.kind === "poll_update"   && data.poll)    { this.renderPoll(data.poll); return }
    if (data.kind === "poll_closed")                   { this.clearPoll();           this._hidePollBadge(); return }
    if (data.kind === "chat_pinned"   && data.message) { this._renderPinnedBar(data.message); return }
    if (data.kind === "chat_unpinned")                 { this._clearPinnedBar(); return }
    if (data.kind === "link_clicked")                  { this._updateLinkCount(data); return }

    if (!this.hasChatMessagesTarget) return

    const content = String(data.content || "")
    const author  = String(data.user    || "Invité")
    const isHost  = !!data.host
    const msgId   = data.id

    const wrapper = document.createElement("div")
    wrapper.className = "lr-chat-row" + (isHost ? " is-host" : "")
    if (msgId) wrapper.dataset.chatMessageId = String(msgId)

    const avatar = document.createElement("div")
    avatar.className = "lr-chat-avatar"
    avatar.style.background = this._avatarColor(author)
    avatar.textContent = this._initials(author)
    wrapper.appendChild(avatar)

    const body = document.createElement("div")
    body.className = "lr-chat-body"

    const authorRow = document.createElement("div")
    authorRow.className = "lr-chat-author"
    const nameEl = document.createElement("strong")
    nameEl.textContent = author
    authorRow.appendChild(nameEl)
    if (isHost) {
      const hostBadge = document.createElement("span")
      hostBadge.className = "lr-chat-host-badge"
      hostBadge.textContent = "HOST"
      authorRow.appendChild(hostBadge)
    }
    body.appendChild(authorRow)

    const text = document.createElement("p")
    text.className = "lr-chat-text"
    // Build links via createElement (NOT innerHTML) so user input can't
    // inject markup. Wires click tracking on each <a>.
    this._appendTextWithLinks(text, content, msgId)
    body.appendChild(text)

    wrapper.appendChild(body)

    if (this.modeValue === "host" && msgId) {
      const actions = document.createElement("div")
      actions.className = "lr-chat-actions"

      const pinBtn = document.createElement("button")
      pinBtn.type = "button"
      pinBtn.className = "lr-chat-pin"
      pinBtn.title = "Épingler ce message"
      pinBtn.dataset.action = "click->live-room#pinChatMessage"
      pinBtn.dataset.messageId = String(msgId)
      const pinIcon = elementIcone("pin")
      pinBtn.appendChild(pinIcon)
      actions.appendChild(pinBtn)

      // Local-only "À traiter" flag — toggles `is-flagged` on the row so the
      // host can mark questions to answer later. Visual state only, not
      // persisted (mirrors hifi-live-host.jsx:177-184 chat moderation).
      const flagBtn = document.createElement("button")
      flagBtn.type = "button"
      flagBtn.className = "lr-chat-flag"
      flagBtn.title = "Marquer à traiter"
      flagBtn.addEventListener("click", () => wrapper.classList.toggle("is-flagged"))
      const flagIcon = elementIcone("flag")
      flagBtn.appendChild(flagIcon)
      actions.appendChild(flagBtn)

      wrapper.appendChild(actions)
    }

    this.chatMessagesTarget.appendChild(wrapper)
    wrapper.scrollIntoView({ behavior: "smooth", block: "end" })

    if (this.hasChatCountTarget) {
      const next = (parseInt(this.chatCountTarget.textContent, 10) || 0) + 1
      this.chatCountTarget.textContent = String(next)
    }
  }

  // Splits a string by URL matches and appends text + <a> nodes to `parent`.
  // Uses createElement / textContent throughout — never innerHTML — so user
  // content can't inject HTML.
  //
  // Matches three forms:
  //   1. https://example.com/path        (explicit scheme)
  //   2. www.example.com                 (www prefix, schemeless)
  //   3. beb.tn /  google.com/search     (bare domain with TLD)
  //
  // The TLD must be 2–24 alphabetic chars, which rules out false positives
  // like "e.g.", "etc.", "v2.0".
  _appendTextWithLinks(parent, text, messageId) {
    // Two alternatives:
    //   1. https?://...  — explicit scheme, always wins
    //   2. bare domain (no scheme) — must NOT be preceded by '@' (email)
    //      and must have at least one non-numeric segment so version
    //      strings like "v2.0.5" or dates "10.05.2026" don't match.
    const re = /\b(https?:\/\/[^\s<>()[\]{}"']+|(?<![@\w.-])(?:www\.)?[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*\.[a-z]{2,24}(?:\/[^\s<>()[\]{}"']*)?)/gi
    let last = 0
    let m
    while ((m = re.exec(text)) !== null) {
      let raw = m[0]
      // Reject bare-domain matches where every dot-segment is fully numeric
      // (e.g. "192.168.1.1", "1.2.3"). The scheme'd form is always allowed.
      if (!/^https?:\/\//i.test(raw)) {
        const before = re.lastIndex - raw.length - 1
        // Belt-and-suspenders: in case the lookbehind isn't supported on
        // very old engines, double-check the preceding char isn't '@' or
        // a word/dot/hyphen char that would indicate a continuation.
        const prev = before >= 0 ? text[before] : ""
        if (prev && /[@\w.-]/.test(prev)) {
          if (m.index > last) parent.appendChild(document.createTextNode(text.slice(last, m.index + raw.length)))
          last = m.index + m[0].length
          continue
        }
        const segments = raw.replace(/\/.*$/, "").split(".")
        const allNumeric = segments.every(s => /^\d+$/.test(s))
        if (allNumeric) {
          if (m.index > last) parent.appendChild(document.createTextNode(text.slice(last, m.index + raw.length)))
          last = m.index + m[0].length
          continue
        }
      }

      if (m.index > last) parent.appendChild(document.createTextNode(text.slice(last, m.index)))
      const trailing = raw.match(/[.,!?:;)]+$/)
      let tail = ""
      if (trailing) { tail = trailing[0]; raw = raw.slice(0, -tail.length) }

      // Schemeless matches (bare domain, www.x.y) get https:// prepended
      // for the href and tracking; the visible text stays as the user typed.
      const display = raw
      const href    = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`

      const a = document.createElement("a")
      a.href = href
      a.target = "_blank"
      a.rel = "noopener noreferrer"
      a.className = "lr-chat-link"
      a.textContent = display
      a.dataset.url = href
      if (messageId) {
        a.dataset.messageId = String(messageId)
        a.addEventListener("click", () => this._trackLinkClick(messageId, href))
      }
      parent.appendChild(a)

      if (this.modeValue === "host" && messageId) {
        const pill = document.createElement("span")
        pill.className = "lr-chat-clicks hidden"
        pill.dataset.url = href
        pill.dataset.messageId = String(messageId)
        const icon = elementIcone("mouse-pointer")
        pill.appendChild(icon)
        const cnt = document.createElement("span")
        cnt.dataset.role = "count"
        cnt.textContent = "0"
        pill.appendChild(cnt)
        parent.appendChild(pill)
      }

      if (tail) parent.appendChild(document.createTextNode(tail))
      last = m.index + m[0].length
    }
    if (last < text.length) parent.appendChild(document.createTextNode(text.slice(last)))
  }

  _trackLinkClick(messageId, url) {
    const headers = { "X-CSRF-Token": this.csrf(), "Content-Type": "application/json" }
    if (this.liveEventTokenValue) headers["X-Live-Event-Token"] = this.liveEventTokenValue
    fetch(`/chat_messages/${messageId}/track_click`, {
      method:  "POST",
      headers,
      body:    JSON.stringify({ url })
    }).catch(() => {})
  }

  _updateLinkCount(data) {
    if (this.modeValue !== "host" || !this.hasChatMessagesTarget) return
    const row = this.chatMessagesTarget.querySelector(`[data-chat-message-id="${data.message_id}"]`)
    if (!row) return
    // Iterate pills and compare data-url in JS rather than building a CSS
    // selector — URLs with brackets/backticks/quotes would crash querySelector.
    const pills = row.querySelectorAll(".lr-chat-clicks")
    for (const pill of pills) {
      if (pill.dataset.url !== data.url) continue
      const cnt = pill.querySelector('[data-role="count"]')
      if (cnt) cnt.textContent = String(data.clicks || 0)
      pill.classList.remove("hidden")
      break
    }
  }

  pinChatMessage(event) {
    const id = event.currentTarget.dataset.messageId
    if (!id || !this.chatSub) return
    this.chatSub.pinMessage(parseInt(id, 10))
  }

  unpinChatMessage() {
    if (this.chatSub) this.chatSub.unpinMessage()
  }

  _renderPinnedBar(message) {
    if (!this.hasPinBarTarget) return
    const bar = this.pinBarTarget
    bar.replaceChildren()

    const icon = elementIcone("pin", { classe: "lr-pin-icon" })
    bar.appendChild(icon)

    const body = document.createElement("div")
    body.className = "lr-pin-body"
    const eyebrow = document.createElement("span")
    eyebrow.className = "lr-pin-eyebrow"
    eyebrow.textContent = "Épinglé"
    body.appendChild(eyebrow)
    const author = document.createElement("span")
    author.className = "lr-pin-author"
    author.textContent = (message.user || "Invité") + " "
    body.appendChild(author)
    const text = document.createElement("span")
    text.className = "lr-pin-text"
    // Pass null as messageId so clicks on links in the pin bar don't
    // double-track (the same message is usually also visible in the
    // scrollable chat and tracks its own clicks there).
    this._appendTextWithLinks(text, String(message.content || ""), null)
    body.appendChild(text)
    bar.appendChild(body)

    if (this.modeValue === "host") {
      const close = document.createElement("button")
      close.type = "button"
      close.className = "lr-pin-close"
      close.title = "Désépingler"
      close.dataset.action = "click->live-room#unpinChatMessage"
      const x = elementIcone("x")
      close.appendChild(x)
      bar.appendChild(close)
    }
  }

  _clearPinnedBar() {
    if (this.hasPinBarTarget) this.pinBarTarget.replaceChildren()
  }

  // Two-letter initials extracted from a display name. Falls back to the
  // first two chars if there's only one token.
  _initials(name) {
    const parts = String(name).trim().split(/\s+/)
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
    return String(name).slice(0, 2).toUpperCase()
  }

  // Cheap deterministic hash → hue 0-360. Same string always maps to
  // the same hue, giving each chatter a stable color without needing
  // a server-side avatar service.
  _avatarColor(seed) {
    let h = 0
    for (let i = 0; i < seed.length; i++) h = ((h << 5) - h) + seed.charCodeAt(i) | 0
    return `oklch(0.55 0.16 ${Math.abs(h) % 360})`
  }

  maybeDisableShareOnIOS() {
    if (!this.hasShareBtnTarget) return
    if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      this.shareBtnTarget.disabled = true
      this.shareBtnTarget.title = "Indisponible sur iOS"
    }
  }

  csrf() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  // ───── Live poll rendering ──────────────────────────────────────────────
  renderPoll(poll) {
    if (!this.hasPollContainerTarget) return
    const root = this.pollContainerTarget
    root.replaceChildren()

    const wrap = document.createElement("div")
    wrap.className = "lr-poll-wrap"

    const eyebrow = document.createElement("div")
    eyebrow.className = "lr-poll-eyebrow"
    eyebrow.textContent = "Sondage en direct"
    wrap.appendChild(eyebrow)

    const q = document.createElement("div")
    q.className = "lr-poll-q"
    q.textContent = poll.question || ""
    wrap.appendChild(q)

    const list = document.createElement("div")
    list.className = "lr-poll-options"
    ;(poll.options || []).forEach((o) => {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "lr-poll-option" + (o.mine ? " is-mine" : "")
      btn.dataset.optionId = o.id

      const fill = document.createElement("div")
      fill.className = "lr-poll-fill"
      fill.style.width = (o.pct || 0) + "%"
      btn.appendChild(fill)

      const body = document.createElement("span")
      body.className = "lr-poll-body"
      body.textContent = o.body
      btn.appendChild(body)

      const pct = document.createElement("strong")
      pct.className = "lr-poll-pct"
      pct.textContent = (o.pct || 0) + " %"
      btn.appendChild(pct)

      btn.addEventListener("click", () => this.castVote(o.id))
      list.appendChild(btn)
    })
    wrap.appendChild(list)

    const total = document.createElement("p")
    total.className = "lr-poll-total"
    total.textContent = (poll.total_votes || 0) + " réponse" + ((poll.total_votes || 0) > 1 ? "s" : "")
    wrap.appendChild(total)

    // Host-only "Clôturer" button — matches design hifi-live-host.jsx:134.
    if (this.modeValue === "host" && poll.id) {
      const close = document.createElement("button")
      close.type = "button"
      close.className = "lr-poll-close"
      close.textContent = "Clôturer le sondage"
      close.addEventListener("click", () => this._closePoll(poll.id))
      wrap.appendChild(close)
    }

    root.appendChild(wrap)
  }

  async _closePoll(id) {
    if (!window.confirm("Clôturer ce sondage et afficher les résultats finaux ?")) return
    try {
      await fetch(`/live_polls/${id}/close`, {
        method: "POST",
        headers: { "X-CSRF-Token": this.csrf(), "Accept": "application/json" }
      })
    } catch (e) {
      console.error("closePoll error:", e)
    }
  }

  clearPoll() {
    if (!this.hasPollContainerTarget) return
    const root = this.pollContainerTarget
    root.replaceChildren()
    if (this.hasPollEmptyTarget) {
      const empty = this.pollEmptyTarget.cloneNode(true)
      root.appendChild(empty)
    }
  }

  async castVote(optionId) {
    if (!optionId) return
    // Guard against double-clicks: without this, a fast user can fire two
    // POSTs to different option IDs before either response lands, and the
    // server creates both rows (the unique index is per-option, not per-poll).
    if (this._votePending) return
    this._votePending = true

    // Anonymous viewers (registration / invitation) authenticate via the
    // `X-Live-Event-Token` request header — keeps the token off URLs, server
    // logs, and analytics. Authenticated users ignore the header.
    const headers = {
      "X-CSRF-Token": this.csrf(),
      "Accept":       "application/json"
    }
    if (this.liveEventTokenValue) headers["X-Live-Event-Token"] = this.liveEventTokenValue

    try {
      const res = await fetch(`/live_poll_options/${optionId}/vote`, { method: "POST", headers })
      if (res.status === 409) {
        // Server enforces single-choice — voter already picked another option.
        this._showBanner("info", "Vous avez déjà voté pour une autre réponse.", { sticky: false })
        setTimeout(() => this._hideBanner(), 3500)
      } else if (res.status === 410) {
        this._showBanner("info", "Sondage clôturé.", { sticky: false })
        setTimeout(() => this._hideBanner(), 3500)
      } else if (!res.ok) {
        this._showBanner("error", "Vote non comptabilisé. Réessayez.", { sticky: false })
        setTimeout(() => this._hideBanner(), 3500)
      } else {
        // Server returns the refreshed poll on success — render so the user
        // sees their vote landed even if the broadcast hasn't arrived yet.
        const poll = await res.json().catch(() => null)
        if (poll?.id) this.renderPoll(poll)
      }
    } catch (_) {
      this._showBanner("error", "Connexion perdue. Vote non envoyé.", { sticky: false })
      setTimeout(() => this._hideBanner(), 3500)
    } finally {
      this._votePending = false
    }
  }
}
